import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import Icon from './AppIcon';

/**
 * ProtectedRoute - Guards routes by authentication and optional RBAC permission checks.
 *
 * Props:
 *   children            - The component to render if access is granted
 *   requiredRole        - (optional) legacy role check: 'admin' | 'estimator' | 'viewer'
 *   requiredPermission  - (optional) legacy permission key
 *   rbacModule          - (optional) RBAC module key (MODULE_KEYS)
 *   rbacPermission      - (optional) RBAC permission key (PERM_KEYS)
 *   requireAdmin        - (optional) require admin or super_admin
 *   redirectTo          - (optional) where to redirect on access denied
 */
const ProtectedRoute = ({
  children,
  requiredRole,
  requiredPermission,
  rbacModule,
  rbacPermission,
  requireAdmin = false,
  redirectTo = '/access-denied',
}) => {
  const { user, loading, profileLoading, userRole, hasPermission: legacyHasPermission, ROLES } = useAuth();
  const { rbacLoading, isAdmin, hasPermission: rbacHasPermission, canAccessModule } = useRBAC();

  const isLoading = loading || profileLoading || rbacLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/sign-in" replace />;

  // Admin bypass
  if (isAdmin) return children;

  // Require admin check
  if (requireAdmin && !isAdmin) {
    return <Navigate to={redirectTo} replace />;
  }

  // RBAC module + permission check
  if (rbacModule && rbacPermission) {
    if (!rbacHasPermission(rbacModule, rbacPermission)) {
      return <Navigate to={redirectTo} replace />;
    }
  } else if (rbacModule) {
    if (!canAccessModule(rbacModule)) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Legacy role hierarchy check
  if (requiredRole) {
    const roleHierarchy = { admin: 3, estimator: 2, viewer: 1 };
    const userLevel = roleHierarchy?.[userRole] || 0;
    const requiredLevel = roleHierarchy?.[requiredRole] || 0;
    if (userLevel < requiredLevel) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Legacy permission check
  if (requiredPermission && !legacyHasPermission(requiredPermission)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute;
